import { Plus } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { EmptyState } from "../components/EmptyState";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import { useSectionSearch } from "../lib/section-search";
import { siteColumns, toSiteRecord } from "../lib/operational-collection-records";
import type { UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";

const kindLabels = {
  residence: "Residence",
  condominium: "Condominio",
  hotel: "Hotel",
  villa: "Villa",
  private_home: "Privato",
  other: "Altro",
} as const;

export function SitesPage({ session }: { session: UserSession }) {
  const utils = trpc.useUtils();
  const sites = trpc.sites.list.useQuery();
  const create = trpc.sites.create.useMutation({ onSuccess: () => void utils.sites.list.invalidate() });
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("strutture");
  const [search, setSearch] = useSectionSearch();
  const canManage = session.membership.role !== "collaborator";
  const records = useMemo(() => (sites.data ?? []).map(toSiteRecord), [sites.data]);
  const filteredRecords = useMemo(
    () => records.filter((record) => matchesCollectionSearch(search, [record.title, ...Object.values(record.cells).map((cell) => cell?.text)])),
    [records, search],
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await create.mutateAsync({
      name: String(data.get("name")),
      kind: String(data.get("kind")) as keyof typeof kindLabels,
      address: String(data.get("address")),
      contactName: String(data.get("contactName") || "") || null,
      contactPhone: String(data.get("contactPhone") || "") || null,
    });
    setOpen(false);
  }

  return (
    <main className="page">
      <header className="page-heading">
        <div><p className="eyebrow">Luoghi di lavoro</p><h1>Strutture</h1><p>Residence, hotel, condomini e abitazioni seguite dalla squadra.</p></div>
        <div className="page-heading__actions">
          <CollectionSearch label="Cerca strutture" value={search} onChange={setSearch} placeholder="Nome, referente o indirizzo" />
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          {canManage && <button className="button button--primary" type="button" onClick={() => setOpen(!open)}><Plus size={19} /> Nuova struttura</button>}
        </div>
      </header>

      {open && (
        <form className="editor-card" onSubmit={submit}>
          <div className="editor-card__heading"><h2>Aggiungi una struttura</h2><p>Inserisci i dati che servono davvero sul campo.</p></div>
          <div className="form-grid">
            <label><span>Nome</span><input name="name" required minLength={2} placeholder="Residence Aurora" /></label>
            <label><span>Tipo</span><select name="kind" defaultValue="residence">{Object.entries(kindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="form-grid__wide"><span>Indirizzo</span><input name="address" required minLength={3} placeholder="Via Bafile 18, Jesolo" /></label>
            <label><span>Referente</span><input name="contactName" placeholder="Nome e cognome" /></label>
            <label><span>Telefono</span><input name="contactPhone" type="tel" placeholder="+39 ..." /></label>
          </div>
          {create.error && <p className="form-error">{create.error.message}</p>}
          <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setOpen(false)}>Annulla</button><button className="button button--primary" disabled={create.isPending}>Salva struttura</button></div>
        </form>
      )}

      <section className="collection-section" data-view={viewMode}>
        {sites.isLoading && <p className="loading-copy">Caricamento strutture...</p>}
        {!sites.isLoading && sites.isError && <EmptyState title="Impossibile caricare le strutture"><button type="button" className="button button--outline" onClick={() => void sites.refetch()}>Riprova</button></EmptyState>}
        {!sites.isLoading && !sites.isError && records.length === 0 && <EmptyState title="Nessuna struttura">Aggiungi il primo luogo di lavoro per iniziare a programmare gli interventi.</EmptyState>}
        {!sites.isLoading && !sites.isError && records.length > 0 && filteredRecords.length === 0 && <EmptyState title="Nessun risultato">Prova a modificare la ricerca.</EmptyState>}
        {!sites.isLoading && !sites.isError && filteredRecords.length > 0 && <CollectionView mode={viewMode} ariaLabel="Strutture" titleLabel="Struttura" columns={siteColumns} records={filteredRecords} print={{ section: "Strutture", details: [search ? `Ricerca: ${search}` : null] }} />}
      </section>
    </main>
  );
}
